import { DEFAULT_BRANDING } from "@/types/ui/branding";
import type { Agent } from "@/types/agent/agent";
import { readableOnWhite } from "@/utils/branding/brandContrast";

/** Append an 8-bit alpha to a 6-digit hex; pass-through otherwise. */
export const withAlpha = (hex: string, aa: string): string =>
  /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${aa}` : hex;

export interface GenieBrandTheme {
  primary: string;
  secondary: string;
  gradientOn: boolean;
  angle: number;
  /** linear-gradient(...) when the genie has gradient branding enabled, else the flat primary color */
  gradient: string;
  withAlpha: (hex: string, aa: string) => string;
}

/** The color slice of a genie's branding, however a surface came by it. */
export interface BrandColorSource {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  gradientEnabled?: boolean | null;
  gradientAngle?: number | null;
}

/**
 * Resolve a brand theme from bare colors — the shared resolution behind every
 * surface that paints in a genie's own brand, including ones (like the public
 * Genie Page) that only hold a sanitized DTO rather than a full Agent.
 *
 * `defaultGradientOn` decides what a missing `gradientEnabled` means: owner
 * surfaces read the full branding object where absence means "off", while the
 * public DTO may not carry the flag at all, in which case the page keeps its
 * gradient whenever a second color exists.
 */
export const getBrandThemeFromColors = (
  source?: BrandColorSource | null,
  options: { defaultGradientOn?: boolean; defaultAngle?: number } = {},
): GenieBrandTheme => {
  const { defaultGradientOn = false, defaultAngle = 135 } = options;
  const primary = source?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const secondaryRaw = source?.secondaryColor || undefined;
  const secondary = secondaryRaw || primary;
  const gradientOn = (source?.gradientEnabled ?? defaultGradientOn) === true && Boolean(secondaryRaw);
  const angle = source?.gradientAngle || defaultAngle;
  const gradient = gradientOn ? `linear-gradient(${angle}deg, ${primary}, ${secondary})` : primary;
  return { primary, secondary, gradientOn, angle, gradient, withAlpha };
};

/** Resolve a genie's own brand colors (falls back to the app default when unset). */
export const getGenieBrandTheme = (agent?: Agent | null): GenieBrandTheme =>
  getBrandThemeFromColors((agent?.branding ?? null) as BrandColorSource | null);

/**
 * A genie's brand theme for the app's own white cards, where the brand is used
 * as text, icons, borders and fills under white labels. Each colour goes through
 * `readableOnWhite`, so a white or pastel brand stays visible; brands that
 * already stand off white resolve exactly as `getGenieBrandTheme` does.
 */
export const getReadableGenieBrandTheme = (agent?: Agent | null): GenieBrandTheme => {
  const theme = getGenieBrandTheme(agent);
  const primary = readableOnWhite(theme.primary);
  const secondary = readableOnWhite(theme.secondary, primary);
  const gradient = theme.gradientOn ? `linear-gradient(${theme.angle}deg, ${primary}, ${secondary})` : primary;
  return { ...theme, primary, secondary, gradient };
};
